import { useMemo, useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import DataTable from '@components/shared/DataTable/DataTable';
import type { DataTableColumn, SortState } from '@components/shared/DataTable/DataTable';
import FilterPanel from '@components/shared/FilterPanel/FilterPanel';
import AppliedFiltersBar from '@components/shared/AppliedFiltersBar/AppliedFiltersBar';
import ProfileActionsMenu from '@components/shared/ProfileActionsMenu/ProfileActionsMenu';
import type { ProfileAction } from '@components/shared/ProfileActionsMenu/ProfileActionsMenu';
import { Forbidden } from '@workpay/ds-shared/components/Forbidden/Forbidden';
import { isForbiddenError } from '@workpay/ds-shared/components/Forbidden/ForbiddenBoundary';
import { WorkpayApiError } from '@lib/apiEnvelope';
import { dsSnapshotService } from '@services/dsSnapshotService';
import { getTenantById, requestTenantActivation } from '@services/tenantService';
import { useNotificationStore } from '@stores/notificationStore';
import { useAuthStore } from '@stores/authStore';
import { useTableFilters } from '@hooks/useTableFilters';
import { useTranslation } from '@hooks/useTranslation';
import { useCountries } from '@hooks/useCountries';
import { getEmploymentTypes } from '@services/employmentTypeService';
import { getDocumentTypes } from '@services/documentTypeService';
import { toPaginationState } from '@utils/tabHelpers';
import { toTitleCase } from '@utils/textCase';
import type { TableSearchParams } from '@utils/tabHelpers';
import type { FilterFieldConfig } from '@/types/filters';
import type { DocumentSnapshot } from '@workpay/ds-shared/types/snapshot';
import { ContractReviewBanner } from './ContractReviewBanner';
import { STATUS_CLASS_MAP, FORMAT_CLASS_MAP } from './snapshotBadges';
import styles from './SnapshotList.module.scss';

type EnrichedSnapshot = DocumentSnapshot & {
  countryName?: string;
  employmentTypeName?: string;
  documentTypeName?: string;
};

interface SnapshotListProps {
  searchParams: TableSearchParams;
}

export function SnapshotList({ searchParams }: SnapshotListProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const session = useAuthStore((s) => s.session);
  const tenantId = session?.tenant.id ?? '';
  const addNotification = useNotificationStore((s) => s.addNotification);

  // Live fetch — the Activate mutation on this page transitions the status
  // mid-session, and session.tenant.status is frozen at login. Don't replace
  // with the session value or the banner stays after activation.
  const { data: tenantData } = useQuery({
    queryKey: ['tenant', tenantId],
    queryFn: () => getTenantById(tenantId),
    enabled: !!tenantId,
    staleTime: 30_000,
  });
  const isContractReviewPending = tenantData?.tenant.status === 'CONTRACT_REVIEW_PENDING';

  const { page: urlPage, size: urlSize, sortBy, sortDir, filters: filtersParam } = searchParams;
  const page = urlPage ?? 0;
  const size = urlSize ?? 10;

  const sort: SortState | undefined = sortBy
    ? { columnId: sortBy, direction: (sortDir ?? 'desc') as 'asc' | 'desc' }
    : undefined;

  const filterFields = useMemo<FilterFieldConfig[]>(() => [
    {
      fieldKey: 'search',
      label: t('K_SNAPSHOT_COLUMN_NAME', 'Contract Name'),
      type: 'text',
      placeholder: t('K_SNAPSHOT_PLACEHOLDER_SEARCH', 'Search contract name...'),
    },
    {
      fieldKey: 'status',
      label: t('K_COLUMN_STATUS', 'Status'),
      type: 'select',
      options: [
        { value: 'IN_REVIEW', label: t('K_SNAPSHOT_STATUS_IN_REVIEW', 'In Review') },
        { value: 'REVIEW_COMPLETED', label: t('K_DS_STATUS_REVIEW_COMPLETED', 'Review Complete') },
        { value: 'ACTIVE', label: t('K_OPTION_ACTIVE', 'Active') },
        { value: 'INACTIVE', label: t('K_OPTION_INACTIVE', 'Inactive') },
      ],
    },
  ], [t]);

  const {
    filters,
    filterPayload,
    hasFilters,
    activeFilterCount,
    isPanelOpen,
    togglePanel,
    closePanel,
    applyFilters,
    removeFilter,
    clearAllFilters,
  } = useTableFilters({
    routeTo: '/t/contracts',
    filtersParam,
    fields: filterFields,
  });

  const sortString = useMemo(() => {
    if (!sort) return 'createdOn,desc';
    return `${sort.columnId},${sort.direction}`;
  }, [sort]);

  const statusFilter = filterPayload?.filters?.status as string | undefined;
  const searchFilter = filterPayload?.search as string | undefined;

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['dsSnapshots', { page, size, sort: sortString, status: statusFilter, search: searchFilter }],
    queryFn: () => dsSnapshotService.search({
      page,
      size,
      sort: sortString,
      search: searchFilter,
      status: statusFilter,
    }),
    refetchInterval: isContractReviewPending ? 30_000 : undefined,
  });
  const listForbidden = isForbiddenError(error);

  // Banner counts must ignore the table's status/search filters, so they run
  // as separate size=1 queries. Only fired while the banner renders.
  const { data: totalCountData } = useQuery({
    queryKey: ['dsSnapshots', 'count', 'all'],
    queryFn: () => dsSnapshotService.search({ page: 0, size: 1 }),
    enabled: isContractReviewPending,
    refetchInterval: 30_000,
  });
  const { data: pendingCountData } = useQuery({
    queryKey: ['dsSnapshots', 'count', 'IN_REVIEW'],
    queryFn: () => dsSnapshotService.search({ page: 0, size: 1, status: 'IN_REVIEW' }),
    enabled: isContractReviewPending,
    refetchInterval: 30_000,
  });
  const bannerTotal = totalCountData?.totalElements ?? 0;
  const bannerActive = Math.max(0, bannerTotal - (pendingCountData?.totalElements ?? 0));

  // Reference data for name resolution
  const { countries } = useCountries();

  const { data: employmentTypes } = useQuery({
    queryKey: ['employmentTypes', 'all'],
    queryFn: () => getEmploymentTypes({ size: 300 }),
  });

  const { data: documentTypes } = useQuery({
    queryKey: ['documentTypes', 'all'],
    queryFn: () => getDocumentTypes({ size: 300 }),
  });

  const countryMap = useMemo(() => new Map(countries.map((c) => [c.id, c.name])), [countries]);
  const empTypeMap = useMemo(() => new Map((employmentTypes?.content ?? []).map((e) => [e.id, e.name])), [employmentTypes]);
  const docTypeMap = useMemo(() => new Map((documentTypes?.content ?? []).map((d) => [d.id, d.name])), [documentTypes]);

  const snapshots = useMemo<EnrichedSnapshot[]>(() =>
    (data?.content ?? []).map((snap) => ({
      ...snap,
      countryName: snap.countryId ? countryMap.get(snap.countryId) : undefined,
      employmentTypeName: snap.employmentTypeId ? empTypeMap.get(snap.employmentTypeId) : undefined,
      documentTypeName: snap.documentTypeId ? docTypeMap.get(snap.documentTypeId) : undefined,
    })),
    [data, countryMap, empTypeMap, docTypeMap],
  );
  const pagination = useMemo(() => toPaginationState(data), [data]);

  // From CONTRACT_REVIEW_PENDING the backend unconditionally flips the tenant to
  // ACTIVATION_PENDING — contract review no longer gates activation. The mutation
  // can still fail on transport / 5xx, so we surface the backend message when
  // available and a generic fallback otherwise.
  const activationMutation = useMutation({
    mutationFn: () => requestTenantActivation(tenantId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tenant', tenantId] });
      void navigate({ to: '/t/employees', replace: true });
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
    },
  });

  const columns = useMemo<DataTableColumn<EnrichedSnapshot>[]>(() => [
    {
      id: 'name',
      header: t('K_SNAPSHOT_COLUMN_NAME', 'Contract Name'),
      sortable: true,
      sortKey: 'name',
      width: '200px',
      cell: (item) => <span className={styles.cellPrimary} title={item.name}>{item.name}</span>,
    },
    {
      id: 'countryName',
      header: t('K_COLUMN_COUNTRY', 'Country'),
      cell: (item) => item.countryName || '\u2014',
    },
    {
      id: 'employmentTypeName',
      header: t('K_COLUMN_EMPLOYMENT_TYPE', 'Employment Type'),
      cell: (item) => item.employmentTypeName || '\u2014',
    },
    {
      id: 'documentTypeName',
      header: t('K_COLUMN_DOCUMENT_TYPE', 'Document Type'),
      cell: (item) => item.documentTypeName || '\u2014',
    },
    {
      id: 'templateFormat',
      header: t('K_DS_COLUMN_FORMAT', 'Format'),
      cell: (item) => (
        <span className={`${styles.badge} ${styles[FORMAT_CLASS_MAP[item.templateFormat] ?? 'badgeNeutral'] ?? ''}`}>
          {item.templateFormat}
        </span>
      ),
    },
    {
      id: 'status',
      header: t('K_COLUMN_STATUS', 'Status'),
      cell: (item) => {
        const label =
          item.status === 'IN_REVIEW'
            ? t('K_SNAPSHOT_STATUS_IN_REVIEW', 'In Review')
            : item.status === 'REVIEW_COMPLETED'
              ? t('K_DS_STATUS_REVIEW_COMPLETED', 'Review Complete')
              : toTitleCase(item.status);
        return (
          <span className={`${styles.badge} ${styles[STATUS_CLASS_MAP[item.status] ?? 'badgeNeutral'] ?? ''}`}>
            {label}
          </span>
        );
      },
    },
    {
      id: 'createdOn',
      header: t('K_COLUMN_CREATED', 'Created'),
      sortable: true,
      sortKey: 'createdOn',
      cell: (item) => new Date(item.createdOn).toLocaleDateString(),
    },
  ], [t]);

  const rowActions = useCallback((item: EnrichedSnapshot) => {
    const actions: ProfileAction[] = [];

    actions.push({
      id: 'view',
      label: t('K_VIEW', 'View'),
      onClick: () => navigate({ to: '/t/contracts/$snapshotId', params: { snapshotId: item.id }, search: { review: false } }),
    });

    if (hasPermission('DOCUMENT_SNAPSHOTS_UPDATE') && item.status === 'IN_REVIEW') {
      actions.push({
        id: 'review',
        label: t('K_REVIEW', 'Review'),
        onClick: () => navigate({ to: '/t/contracts/$snapshotId', params: { snapshotId: item.id }, search: { review: true } }),
      });
    }

    if (actions.length === 0) return null;
    return <ProfileActionsMenu actions={actions} />;
  }, [t, navigate, hasPermission]);

  const handleSortChange = useCallback((newSort: SortState | undefined) => {
    void navigate({
      to: '/t/contracts',
      search: (prev: TableSearchParams) => ({
        ...prev,
        page: 0,
        sortBy: newSort?.columnId,
        sortDir: newSort?.direction,
      }),
      replace: true,
    });
  }, [navigate]);

  const handlePageChange = useCallback((newPage: number) => {
    void navigate({
      to: '/t/contracts',
      search: (prev: TableSearchParams) => ({ ...prev, page: newPage }),
      replace: true,
    });
  }, [navigate]);

  const handlePageSizeChange = useCallback((newSize: number) => {
    void navigate({
      to: '/t/contracts',
      search: (prev: TableSearchParams) => ({ ...prev, page: 0, size: newSize }),
      replace: true,
    });
  }, [navigate]);

  const handleRowClick = useCallback((item: EnrichedSnapshot) => {
    navigate({ to: '/t/contracts/$snapshotId', params: { snapshotId: item.id }, search: { review: false } });
  }, [navigate]);

  const getRowKey = (item: EnrichedSnapshot) => item.id;

  const emptyState = useMemo(
    () =>
      listForbidden ? (
        <Forbidden variant="empty-state" hideAction />
      ) : (
        <div className={styles.emptyState}>
          <p>{t('K_SNAPSHOT_EMPTY_TITLE', 'No contracts')}</p>
          <p>{t('K_SNAPSHOT_EMPTY_DESC', 'No contract documents have been assigned to your company.')}</p>
        </div>
      ),
    [listForbidden, t],
  );

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerInfo}>
          <h1 className={styles.title}>{t('K_PAGE_TITLE_CONTRACTS', 'Contract Documents')}</h1>
          <p className={styles.subtitle}>{t('K_PAGE_SUBTITLE_CONTRACTS', 'Review and approve your contract documents')}</p>
        </div>
      </div>

      {isContractReviewPending && (
        <ContractReviewBanner
          active={bannerActive}
          total={bannerTotal}
          onRequestActivation={() => activationMutation.mutate()}
          isActivating={activationMutation.isPending}
        />
      )}

      {hasFilters && (
        <AppliedFiltersBar
          fields={filterFields}
          values={filters}
          onRemove={removeFilter}
          onClearAll={clearAllFilters}
        />
      )}

      <DataTable
        columns={columns}
        data={snapshots}
        rowKey={getRowKey}
        onRowClick={handleRowClick}
        loading={isLoading}
        fetching={isFetching}
        sort={sort}
        onSortChange={handleSortChange}
        pagination={pagination}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        showToolbar
        onFilterClick={togglePanel}
        activeFilterCount={activeFilterCount}
        rowActions={rowActions}
        emptyState={emptyState}
      />

      <FilterPanel
        isOpen={isPanelOpen}
        onClose={closePanel}
        fields={filterFields}
        values={filters}
        onApply={applyFilters}
        onReset={clearAllFilters}
        title={t('K_FILTER_TITLE', 'Filters')}
      />
    </div>
  );
}
